import { lazy } from 'react';

const RELOAD_FLAG = 'app-reloaded-on-chunk-error';
const IMPORT_TIMEOUT_MS = 30000;
const REACHABILITY_TIMEOUT_MS = 3000;

export function isStaleChunkError(err) {
    const msg = err?.message || '';
    return (
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('Unable to preload CSS') ||
        msg.includes('Unable to preload module') ||
        msg.includes('Import timeout')
    );
}

/**
 * Reload for a stale chunk, at most once per session (the flag clears on the next successful chunk load). Returns
 * false when this tab already tried, so the caller shows the "Update available" card instead of looping.
 */
export function reloadOnceForStaleChunk() {
    try {
        if (sessionStorage.getItem(RELOAD_FLAG)) return false;
        sessionStorage.setItem(RELOAD_FLAG, 'true');
    } catch {
        return false;
    }
    window.location.reload();
    return true;
}

// Vite reports a failed preload (a CSS file or module a route needs, gone after a deploy) here rather than through
// the import - the same stale-deploy case, so the same one reload. Without it, the page renders unstyled or throws.
if (typeof window !== 'undefined') {
    window.addEventListener('vite:preloadError', (event) => {
        if (reloadOnceForStaleChunk()) event.preventDefault();
    });
}

/**
 * Races a promise against a timeout. Caps how long we wait for a chunk import
 * on slow networks before giving up, so the user doesn't sit staring at a
 * blank screen for the browser's default 60–90s fetch timeout.
 */
function withTimeout(promise, ms) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new Error('Import timeout: chunk took too long to load')),
            ms
        );
        promise.then(
            (val) => { clearTimeout(timer); resolve(val); },
            (err) => { clearTimeout(timer); reject(err); }
        );
    });
}

/**
 * Quick server reachability check. HEAD request to "/" with a short timeout.
 *
 * Used before window.location.reload() to avoid the "blank page forever"
 * symptom when the VPN is down — without this check, reload() triggers a new
 * index.html fetch that hangs indefinitely because packets can't reach the
 * server.
 */
async function canReachServer() {
    try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), REACHABILITY_TIMEOUT_MS);
        const res = await fetch('/', {
            method: 'HEAD',
            signal: controller.signal,
            cache: 'no-store'
        });
        clearTimeout(timer);
        // Any HTTP response means the server is reachable. Even a 404 or
        // redirect is fine — we only care that packets got there and back.
        return res.status < 500;
    } catch {
        return false;
    }
}

/**
 * Wrapper around React.lazy() that handles chunk-load failures gracefully.
 *
 * Behavior:
 *   - Chunk load error (stale deploy, CSS preload, network blip, timeout):
 *     auto-reload ONCE per session to fetch a fresh index.html.
 *   - Before reloading, verify the server is actually reachable. If not
 *     (e.g. VPN is down), throw a distinct "Network unreachable" error so
 *     ChunkErrorBoundary can show a connection-specific error page instead
 *     of silently hanging on an impossible reload.
 *   - Other transient errors: retry once after 1s.
 *   - Imports are capped at 30s so slow/broken networks fail fast.
 *
 * The sessionStorage flag is cleared on any successful load, so a user who
 * keeps their tab open through multiple deploys gets one auto-recovery
 * attempt per deploy.
 */
export function lazyWithRetry(importFn) {
    return lazy(async () => {
        try {
            const module = await withTimeout(importFn(), IMPORT_TIMEOUT_MS);
            sessionStorage.removeItem(RELOAD_FLAG);
            return module;
        } catch (err) {
            if (isStaleChunkError(err)) {
                if (!sessionStorage.getItem(RELOAD_FLAG)) {
                    // Don't blindly reload — if the VPN is down, the reload
                    // will just hang forever. Check reachability first.
                    const reachable = await canReachServer();
                    if (!reachable) {
                        throw new Error(
                            'Network unreachable: server did not respond. ' +
                            'Check your VPN or WiFi connection.'
                        );
                    }
                    sessionStorage.setItem(RELOAD_FLAG, 'true');
                    window.location.reload();
                    return new Promise(() => {}); // hang until reload completes
                }
                throw err;
            }

            // Non-chunk error — retry once after a short delay (also capped)
            await new Promise(r => setTimeout(r, 1000));
            return withTimeout(importFn(), IMPORT_TIMEOUT_MS);
        }
    });
}
